import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import { useNativeBridge } from '@/lib/native-context';
import { useInitialSyncActions } from '@/lib/initial-sync-context';

/**
 * Local-mirror builder. Fetches every entity the user is going to need
 * to function offline and writes the results into the SQLite-backed
 * query cache. Together with the TanStack Query persister, this turns
 * the desktop app into a true local-first ERP:
 *
 *   - First online launch → mirror is built (blocking, with progress).
 *   - Every subsequent launch (online OR offline) → all the lists
 *     hydrate from disk before any network request fires.
 *   - Background "freshness" sync → re-runs the same queries every
 *     5 minutes while online so the mirror tracks the server.
 *   - Reconnect after offline → outbox flushes via the sync engine,
 *     then the mirror refresh picks up the new server state.
 *
 * Strategy:
 *   - Only pre-warm when online and authenticated.
 *   - Sequential, one-at-a-time: don't melt a slow connection by
 *     firing 20 parallel requests.
 *   - Big lists (articles, sales, purchases) are paginated until the
 *     server returns a short page — no silent ~1000-row cap.
 *   - Per-task failures are swallowed — pre-warm is best-effort. The
 *     fallback is "data shows up the moment you visit that page online".
 *   - Progress is published via `InitialSyncContext` so a gate can
 *     show "Synchronisation initiale 23 / 47…" on first login.
 */

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 min
// Sales/purchase docs embed `sales_document_lines(*)` in every row, so a
// 500-doc page can pull thousands of joined rows and exceed Supabase's
// statement timeout on tenants with large histories. 100 keeps each
// page well under the timeout while still finishing the mirror quickly.
const PAGINATED_PAGE_SIZE = 100;
const PAGINATED_SAFETY_CAP = 50_000;

// Hard cap on how long any single prewarm fetch can hang before we move
// on. CRITICAL: without this, a stuck request becomes the CANONICAL
// queryFn for that query key (TanStack uses whichever queryFn registered
// first). Any other component that later attaches to the same key —
// even one with its own timeout — inherits the hung promise. That was
// the post-login "stuck loading" bug: BrandThemeProvider mounted
// ['company-settings'] with its own 7s timeout, but prewarm had already
// registered a no-timeout fetch a few ms earlier, and BrandThemeProvider
// silently waited forever for it.
const PREWARM_FETCH_TIMEOUT_MS = 15_000;

function withPrewarmTimeout<T>(promise: Promise<T>): Promise<T> {
  if (typeof window === 'undefined') return promise;
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('prewarm fetch timeout'));
    }, PREWARM_FETCH_TIMEOUT_MS);
    promise.then(
      (v) => {
        window.clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(timer);
        reject(e);
      },
    );
  });
}

type SinglePrewarmTask = {
  kind: 'single';
  label: string;
  key: readonly unknown[];
  fn: () => Promise<unknown>;
};

type PaginatedPrewarmTask<T extends { id: string }> = {
  kind: 'paginated';
  label: string;
  key: readonly unknown[];
  page: (offset: number, limit: number) => Promise<T[]>;
};

type PrewarmTask = SinglePrewarmTask | PaginatedPrewarmTask<{ id: string }>;

async function runPaginated<T extends { id: string }>(
  qc: QueryClient,
  task: PaginatedPrewarmTask<T>,
  shouldStop: () => boolean,
): Promise<void> {
  const all: T[] = [];
  let offset = 0;
  while (offset < PAGINATED_SAFETY_CAP) {
    if (shouldStop()) return;
    const page = await withPrewarmTimeout(task.page(offset, PAGINATED_PAGE_SIZE));
    all.push(...page);
    if (page.length < PAGINATED_PAGE_SIZE) break;
    offset += PAGINATED_PAGE_SIZE;
  }
  qc.setQueryData(task.key, all);
}

export function usePrewarmCache() {
  const ds = useDataService();
  const qc = useQueryClient();
  const { user } = useAuth();
  const bridge = useNativeBridge();
  const sync = useInitialSyncActions();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const shouldStop = () => cancelled || !bridge.network.isOnline();

    const tasks: PrewarmTask[] = [
      // NOTE: company-settings intentionally NOT prewarmed here. BrandThemeProvider
      // mounts that query with its own 7s timeout as a hard gate ("brand boot")
      // — prewarming it here used to register a no-timeout queryFn that
      // hijacked the key, so BrandThemeProvider's timeout never got a chance
      // to fire and the post-login splash hung forever. Let BrandThemeProvider
      // own that key.
      { kind: 'single', label: 'Paramètres modules', key: ['module-settings'], fn: () => ds.settings.getModuleSettings() },
      { kind: 'single', label: 'Paramètres boutique', key: ['store-settings'], fn: () => ds.storeSettings.get() },
      { kind: 'single', label: 'Thème boutique', key: ['store-theme'], fn: () => ds.storeTheme.get() },
      { kind: 'single', label: 'Configuration', key: ['app-builder', 'tenant'], fn: () => ds.appConfig.getTenantLatest() },

      // Reference / referential.
      { kind: 'single', label: 'Catégories de paiement', key: ['payment-categories', { includeInactive: false }], fn: () => ds.paymentCategories.list(false) },
      { kind: 'single', label: 'Banques', key: ['tn-banks'], fn: () => ds.treasury.listBanks() },

      // Big lists — paginated so >1000 rows don't get truncated by Supabase's default.
      {
        kind: 'paginated',
        label: 'Articles',
        key: ['articles', undefined],
        page: (offset, limit) => ds.articles.list({ offset, limit }) as Promise<Array<{ id: string }>>,
      },
      { kind: 'single', label: 'Catégories articles', key: ['article-categories'], fn: () => ds.articleCategories.list() },
      { kind: 'single', label: 'Familles articles', key: ['article-families'], fn: () => ds.articleFamilies.list() },
      { kind: 'single', label: 'Marques articles', key: ['article-brands'], fn: () => ds.articleBrands.list() },
      { kind: 'single', label: 'Prochain code article', key: ['articles', 'next-code'], fn: () => ds.articles.peekNextCode() },
      { kind: 'single', label: 'Prochain code-barres', key: ['articles', 'next-barcode'], fn: () => ds.articles.peekNextBarcode() },
      { kind: 'single', label: 'Prochain code client', key: ['clients', 'next-code'], fn: () => ds.clients.peekNextCode() },
      { kind: 'single', label: 'Prochain code fournisseur', key: ['suppliers', 'next-code'], fn: () => ds.suppliers.peekNextCode() },

      // Third parties.
      { kind: 'single', label: 'Clients', key: ['clients', undefined], fn: () => ds.clients.list() },
      { kind: 'single', label: 'Fournisseurs', key: ['suppliers', undefined], fn: () => ds.suppliers.list() },
      { kind: 'single', label: 'Représentants', key: ['representatives'], fn: () => ds.representatives.list() },

      // Treasury + payments + checks.
      { kind: 'single', label: 'Comptes bancaires', key: ['bank-accounts'], fn: () => ds.treasury.listBankAccounts() },
      { kind: 'single', label: 'Mouvements bancaires', key: ['bank-transactions', undefined], fn: () => ds.treasury.listTransactions() },
      { kind: 'single', label: 'Paiements', key: ['payments', undefined], fn: () => ds.payments.list() },
      { kind: 'single', label: 'Paiements récurrents', key: ['recurring-payments', undefined], fn: () => ds.recurringPayments.list() },
      { kind: 'single', label: 'Chèques', key: ['checks-drafts', undefined], fn: () => ds.checks.list() },

      // Stock.
      { kind: 'single', label: 'Dépôts', key: ['warehouses'], fn: () => ds.stock.listWarehouses() },
      { kind: 'single', label: 'Stock global', key: ['stock', 'all', undefined], fn: () => ds.stock.getAllStock() },

      // Sales / purchase documents — paginated.
      {
        kind: 'paginated',
        label: 'Documents de vente',
        key: ['sales', undefined],
        page: (offset, limit) => ds.sales.listDocuments({ offset, limit }) as Promise<Array<{ id: string }>>,
      },
      {
        kind: 'paginated',
        label: 'Documents d’achat',
        key: ['purchases', undefined],
        page: (offset, limit) => ds.purchases.listDocuments({ offset, limit }) as Promise<Array<{ id: string }>>,
      },

      // Caisse.
      { kind: 'single', label: 'Sessions de caisse', key: ['cash_sessions', undefined], fn: () => ds.caisse.listSessions() },
      { kind: 'single', label: 'Caisses ouvertes', key: ['cash_sessions', 'open-list'], fn: () => ds.caisse.listOpenSessions() },

      // Withholding (RAS).
      { kind: 'single', label: 'Certificats RAS', key: ['withholding', 'certificates', undefined], fn: () => ds.withholding.listCertificates() },
      { kind: 'single', label: 'Déclarations RAS', key: ['withholding', 'declarations', undefined], fn: () => ds.withholding.listDeclarations() },
      { kind: 'single', label: 'Types opération RAS', key: ['withholding', 'operation-types', undefined], fn: () => ds.withholding.listOperationTypes() },
      { kind: 'single', label: 'Types opération RAS (ventes)', key: ['withholding', 'operation-types', 'sale'], fn: () => ds.withholding.listOperationTypes({ applicable: 'sale' }) },
      { kind: 'single', label: 'Types opération RAS (achats)', key: ['withholding', 'operation-types', 'purchase'], fn: () => ds.withholding.listOperationTypes({ applicable: 'purchase' }) },

      // Settings tables that drive prints + delivery.
      { kind: 'single', label: 'Modèles PDF', key: ['pdf-templates'], fn: () => ds.pdfTemplates.list() },
      { kind: 'single', label: 'Transporteurs', key: ['delivery-providers'], fn: () => ds.deliveryProviders.list() },
      { kind: 'single', label: 'Transporteur par défaut', key: ['delivery-providers', 'default'], fn: () => ds.deliveryProviders.getDefault() },
      { kind: 'single', label: 'Membres équipe', key: ['team-members'], fn: () => ds.team.list() },
      { kind: 'single', label: 'Invitations', key: ['invitations'], fn: () => ds.invitations.list() },
      { kind: 'single', label: 'Rôles', key: ['roles'], fn: () => ds.roles.list() },

      // Inventories.
      { kind: 'single', label: 'Inventaires', key: ['inventories', undefined], fn: () => ds.stock.listInventories() },

      // Stock movements.
      { kind: 'single', label: 'Mouvements de stock', key: ['stock', 'movements', undefined, undefined], fn: () => ds.stock.listMovements() },

      // Resources.
      { kind: 'single', label: 'Dossiers ressources', key: ['resource-folders'], fn: () => ds.resources.listFolders() },
      { kind: 'single', label: 'Fichiers ressources', key: ['resource-files', undefined], fn: () => ds.resources.listFiles() },

      // Storefront.
      { kind: 'single', label: 'Catégories boutique', key: ['storefront-categories', undefined], fn: () => ds.storefrontCategories.list() },
      { kind: 'single', label: 'Produits boutique', key: ['storefront-products', undefined], fn: () => ds.storefrontProducts.list() },
      { kind: 'single', label: 'Clients en ligne', key: ['online-customers', undefined], fn: () => ds.onlineCustomers.list() },
      { kind: 'single', label: 'Bannières boutique', key: ['store-banners'], fn: () => ds.storeBanners.list() },
      { kind: 'single', label: 'Pages boutique', key: ['store-pages'], fn: () => ds.storePages.list() },
      { kind: 'single', label: 'Champs commande boutique', key: ['store-checkout-fields'], fn: () => ds.storeCheckoutFields.list() },
      { kind: 'single', label: 'Notifications admin', key: ['admin-notifications', { limit: 50 }], fn: () => ds.adminNotifications.list({ limit: 50 }) },
      { kind: 'single', label: 'Notifications non lues', key: ['admin-notifications', 'unread-count', undefined], fn: () => ds.adminNotifications.getUnreadCount() },
      { kind: 'single', label: 'Paniers abandonnés', key: ['abandoned-carts', undefined], fn: () => ds.abandonedCarts.list() },

      // Formations.
      { kind: 'single', label: 'Formations', key: ['formation-pages'], fn: () => ds.formationPages.list() },

      // Espace Pro.
      { kind: 'single', label: 'Pro: clients', key: ['admin-pro-customers'], fn: () => ds.proCustomers.listForTenant() },
      { kind: 'single', label: 'Pro: clients en attente', key: ['admin-pro-customers', 'pending'], fn: () => ds.proCustomers.listForTenant({ status: 'pending' }) },
      { kind: 'single', label: 'Pro: commandes', key: ['admin-pro-commandes', 'all'], fn: () => ds.proCommandes.list() },
      { kind: 'single', label: 'Pro: commandes en attente', key: ['admin-pro-commandes', 'en_attente'], fn: () => ds.proCommandes.list({ status: 'en_attente' }) },
      { kind: 'single', label: 'Pro: devis', key: ['admin-pro-devis', ''], fn: () => ds.proDevis.list() },
      { kind: 'single', label: 'Pro: portefeuille', key: ['admin-pro-wallet-movements'], fn: () => ds.proWallet.listMovements() },
      { kind: 'single', label: 'Pro: commissions en attente', key: ['admin-pro-wallet-movements', 'pending-commission'], fn: () => ds.proWallet.listMovements({ status: 'pending', type: 'commission' }) },
      { kind: 'single', label: 'Pro: tarification articles', key: ['pro-article-pricing'], fn: () => ds.proArticlePricing.listAll() },
    ];

    // Track whether this run is the FIRST pass for this user (so the gate
    // can block until done) vs. a routine 5-minute refresh.
    let isFirstPassForUser = !sync.hasUserSynced(user.id);

    const runOnce = async () => {
      if (!bridge.network.isOnline()) return;
      // Report begin only on the first pass — refresh passes shouldn't
      // re-trigger the blocking UI.
      if (isFirstPassForUser) sync.begin(tasks.length);
      for (const t of tasks) {
        if (shouldStop()) return;
        let ok = true;
        try {
          if (t.kind === 'paginated') {
            await runPaginated(qc, t, shouldStop);
          } else {
            await qc.prefetchQuery({
              queryKey: t.key,
              queryFn: () => withPrewarmTimeout(t.fn()),
              staleTime: REFRESH_INTERVAL_MS,
            });
          }
        } catch {
          ok = false;
        }
        if (isFirstPassForUser) sync.step(ok, t.label);
      }
      if (isFirstPassForUser) {
        sync.finish();
        sync.markUserSynced(user.id);
        // Future runs in this useEffect lifetime are refreshes, not first sync.
        isFirstPassForUser = false;
      }
    };

    // First run immediately, then refresh every 5 minutes while online.
    // The interval handler defers to the next macrotask: runOnce kicks off
    // ~60 prefetchQuery registrations whose synchronous portion (TanStack
    // cache + persister bookkeeping) can exceed Chrome's 50ms setInterval
    // budget. Scheduling via setTimeout(0) makes the handler itself a no-op.
    void runOnce();
    const timer = setInterval(() => {
      window.setTimeout(() => void runOnce(), 0);
    }, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [user, ds, qc, bridge.network, sync]);
}
